// Web-only helpers for the live-capture consoles (colours, formatting,
// detail-form requests). The pure engine/catalogue stay platform-neutral and
// byte-identical with mobile - nothing here should move into them.
import moment from 'moment'
import { ACTIONS, LEGACY_EVENT_LABELS } from './catalogue'
import {
  LIVE_ENTRY_META_KEYS,
  LOG_KEYS,
  SECTION_PATHS,
  applyTap,
  buildTap,
  describeEvent,
  formatClock,
  getAction,
} from './engine'

export const TONES = {
  red: { main: '#C62828', dark: '#8E0000', bg: '#FFEBEE' },
  amber: { main: '#E65100', dark: '#AC1900', bg: '#FFF3E0' },
  green: { main: '#2E7D32', dark: '#005005', bg: '#E8F5E9' },
  blue: { main: '#1565C0', dark: '#003C8F', bg: '#E3F2FD' },
  teal: { main: '#00796B', dark: '#004C40', bg: '#E0F2F1' },
  purple: { main: '#6A1B9A', dark: '#38006B', bg: '#F3E5F5' },
  coral: { main: '#D84315', dark: '#9F0000', bg: '#FBE9E7' },
  graphite: { main: '#37474F', dark: '#102027', bg: '#ECEFF1' },
}

export const toneOf = (tone) => TONES[tone] || TONES.graphite

// Keys never shown when an entry is printed as label: value pairs
export const ENTRY_HIDDEN_KEYS = [
  ...LIVE_ENTRY_META_KEYS,
  'isChecklist',
  'moduleType',
  'medicineLogId',
]

export const isHiddenEntryKey = (key, extra = []) =>
  ENTRY_HIDDEN_KEYS.includes(key) || extra.includes(key)

export const visibleEntryKeys = (entry, extra = []) =>
  Object.keys(entry || {}).filter(
    (key) =>
      !isHiddenEntryKey(key, extra) &&
      entry[key] !== undefined &&
      entry[key] !== null &&
      entry[key] !== '',
  )

// Helvetica (react-pdf) has no glyphs for these - swap them for ASCII
export const pdfSafe = (value) =>
  typeof value === 'string'
    ? value
        .replace(/≥/g, '>=')
        .replace(/≤/g, '<=')
        .replace(/→/g, '->')
        .replace(/₂/g, '2')
        .replace(/[‘’]/g, "'")
    : value

export const fmtTime = (time) => (time ? moment(time).format('HH:mm:ss') : '')

export const fmtOffset = (time, start) => {
  if (!time || !start) return ''
  const seconds = Math.floor(
    (new Date(time).getTime() - new Date(start).getTime()) / 1000,
  )
  return seconds < 0 ? `-${formatClock(-seconds)}` : `+${formatClock(seconds)}`
}

export const timeAgo = (time, now = Date.now()) => {
  if (!time) return ''
  const seconds = Math.max(
    0,
    Math.floor((now - new Date(time).getTime()) / 1000),
  )
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`
}

// describeEvent, plus labels for older mobile arrest events
export const describeLiveEvent = (event) => {
  if (!event) return ''
  if (!event.label && !getAction(event.type)) {
    return describeEvent({
      ...event,
      label: LEGACY_EVENT_LABELS[event.type] || event.type,
    })
  }
  return describeEvent(event)
}

// Earliest start across both logs (for "+mm:ss" offsets on merged timelines)
export const liveStartOf = (record, source) => {
  if (source) return record?.[LOG_KEYS[source]]?.startedAt
  const starts = Object.values(LOG_KEYS)
    .map((logKey) => record?.[logKey]?.startedAt)
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
  return starts.length ? new Date(Math.min(...starts)).toISOString() : null
}

export const hasLiveEvents = (record) =>
  Object.values(LOG_KEYS).some(
    (logKey) => (record?.[logKey]?.events || []).length > 0,
  )

export const logKeyForEvent = (record, eventId) =>
  Object.values(LOG_KEYS).find((logKey) =>
    (record?.[logKey]?.events || []).some((event) => event.id === eventId),
  ) || null

// Names of the crew on the record, for 'staff' fields
export const staffNames = (formValues) =>
  (formValues?.incident?.staffInvolved || [])
    .map((staff) => {
      const account = staff?.userAccount || staff
      return [account?.firstName, account?.lastName].filter(Boolean).join(' ')
    })
    .filter(Boolean)

// Append an entry to a section list (immutable)
export const appendEntry = (record, section, entry) => {
  const [a, b] = SECTION_PATHS[section]
  return {
    ...record,
    [a]: { ...(record?.[a] || {}), [b]: [...(record?.[a]?.[b] || []), entry] },
  }
}

// Detail-form requests (what LiveDetailForm opens)
// { section, entryId, eventId, formKey, create }
export const requestForPending = (item) => ({
  section: item.section,
  entryId: item.entry?.entryId,
  eventId: item.event?.id || item.entry?.liveEventId,
  formKey: item.action?.detailForm,
})

export const requestForEntry = (section, entry) => {
  const action = getAction(entry?.liveAction)
  if (!action?.detailForm) return null
  return {
    section,
    entryId: entry.entryId,
    eventId: entry.liveEventId,
    formKey: action.detailForm,
  }
}

export const requestForEvent = (event) => {
  const action = getAction(event?.type)
  if (!action?.detailForm) return null
  return {
    section: event.section || action.target?.section,
    entryId: event.entryId,
    eventId: event.id,
    formKey: action.detailForm,
  }
}

// Start the arrest log: startedAt, cycle length from the org
// protocol, the console level, plus a CPR-on event. Returns an updater for
// setFormValues - the tap is built once, outside the updater.
export const buildArrestStart = ({ level, cycleLengthSeconds, userId }) => {
  const now = new Date().toISOString()
  const tap = buildTap({
    actionKey: 'cpr',
    on: true,
    time: now,
    phase: 'arrest',
    category: 'cardiacArrest',
    userId,
    log: [],
  })
  return (record) => {
    const existing = record?.cardiacArrest
    // Already running (another tab / device got there first): leave it
    if (existing?.startedAt) return record
    const started = {
      ...record,
      cardiacArrest: {
        ...(existing || {}),
        startedAt: now,
        cycleLengthSeconds: Number(cycleLengthSeconds) || 120,
        level,
        events: existing?.events || [],
      },
    }
    return applyTap(started, LOG_KEYS.arrest, tap)
  }
}

export const isArrestInProgress = (record, getPhase) => {
  const log = record?.cardiacArrest
  if (!log?.startedAt) return false
  const ageMs = Date.now() - new Date(log.startedAt).getTime()
  return ageMs < 6 * 3600 * 1000 && getPhase(log.events) !== 'ended'
}

export const actionsWithClocks = Object.keys(ACTIONS).filter(
  (key) => ACTIONS[key].clock,
)
